import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/component.css';
import Icon from "../media/icon/icons.jsx";
import AxipaysFull_log from '../media/image/axi_wideLogo.png';

function Sidebar() {
    const role = sessionStorage.getItem('role');          // "admin" | "client" | "employee"
    const clientId = sessionStorage.getItem('clientId');  // string or null
    const userId = sessionStorage.getItem('userId');      // string or null
    const behavior = sessionStorage.getItem('behavior');  // e.g. "support" | "finance"
    const location = useLocation();

    const [activeItem, setActiveItem] = useState("");
    const [openSubmenus, setOpenSubmenus] = useState({});
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [searchText, setSearchText] = useState(sessionStorage.getItem("sidebarSearch") || "");

    useEffect(() => {
        const interval = setInterval(() => {
            const storedSearch = sessionStorage.getItem("sidebarSearch") || "";
            setSearchText(storedSearch);
        }, 300); // poll every 300ms
        return () => clearInterval(interval);
    }, []);

    const toggleSidebar = () => setMobileSidebarOpen(prev => !prev);

    // ---------- MENU MODEL ----------
    const sidebarItems = useMemo(() => ({
        referral: [
            { section: "Dashboard", items: [{ name: "Overview", icon: "overview", link: "/home" }] },
            
        ],
    }), [clientId]);

    // Select the sections for the current role (and behavior for employees)
    const sections = useMemo(() => {
        
        if (role === 'referral') return sidebarItems.referral;
        
        return [];
    }, [role, behavior, sidebarItems]);

    // ---------- ACTIVE ITEM RESOLUTION ----------
    useEffect(() => {
        const currentPath = location.pathname;

        const findActiveItem = (itemsArray) => {
            for (const section of itemsArray) {
                for (const item of section.items) {
                    if (!item) continue;
                    if (item.link && item.link === currentPath) return item.name;
                    if (item.submenu && Array.isArray(item.submenu)) {
                        for (const subItem of item.submenu) {
                            if (subItem.link && subItem.link === currentPath) return subItem.name;
                        }
                    }
                }
            }
            return null;
        };

        const active = findActiveItem(sections);
        if (active) setActiveItem(active);
    }, [location, sections]);

    // ---------- HELPERS ----------
    const handleItemClick = (itemName, hasSubmenu = false) => {
        if (hasSubmenu) {
            setOpenSubmenus(prev => ({ ...prev, [itemName]: !prev[itemName] }));
        } else {
            setActiveItem(itemName);
        }
    };

    const isItemMatched = (itemName) =>
        searchText.trim() && itemName.toLowerCase().includes(searchText.toLowerCase());

    const isExternal = (link) => typeof link === 'string' && /^https?:\/\//i.test(link);

    const SidebarItem = ({ item }) => {
        const isMatched = isItemMatched(item.name);

        const handleClick = (e) => {
            if (item.disabled) {
                e.preventDefault();
                return;
            }
            if (item.dropdown) {
                e.preventDefault(); // expand/collapse only
                handleItemClick(item.name, true);
            } else {
                handleItemClick(item.name, false);
            }
        };

        const content = (
            <>
                <i className="icon">
                    <Icon name={item.icon} width={22} height={22} color={item.disabled ? "grey" : "black"} />
                </i>
                <span className={`sidebar-item-name ${item.disabled ? "disabled-text" : ""}`}>{item.name}</span>
                {item.dropdown && (
                    <i className="icon icon2">
                        <Icon
                            name={openSubmenus[item.name] ? "keyboard_arrow_down" : "arrow_right"}
                            width={22}
                            height={22}
                            color={item.disabled ? "grey" : "black"}
                        />
                    </i>
                )}
            </>
        );

        // External links use <a>, internal use <Link>
        const className = `sidebar-item ${activeItem === item.name ? "active" : ""} ${item.disabled ? "disabled" : ""} ${item.dropdown ? "expandable" : ""} ${isMatched ? "highlight-sidebar" : ""}`;

        return (
            <div>
                {isExternal(item.link)
                    ? (
                        <a
                            href={item.disabled ? "#" : item.link}
                            className={className}
                            onClick={handleClick}
                            target={item.target || "_blank"}
                            rel="noopener noreferrer"
                        >
                            {content}
                        </a>
                    ) : (
                        <Link
                            to={item.disabled ? "#" : (item.link || "#")}
                            className={className}
                            onClick={handleClick}
                            target={item.target}
                        >
                            {content}
                        </Link>
                    )
                }

                {item.dropdown && openSubmenus[item.name] && !item.disabled && Array.isArray(item.submenu) && (
                    <div className="submenu">
                        {item.submenu.map((subItem) => (
                            <Link
                                key={subItem.name}
                                to={subItem.link || "#"}
                                className={`submenu-item ${activeItem === subItem.name ? "active" : ""}`}
                                onClick={() => handleItemClick(subItem.name, false)}  //{/* set active, don't toggle */}
                            >
                                <span>{subItem.name}</span>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="sidebar-toggle-btn" onClick={toggleSidebar}>
                <Icon name={mobileSidebarOpen ? "arrow_left" : "arrow_right"} width={24} height={24} color="#003366" />
            </div>

            <div className={`sidebar ${mobileSidebarOpen ? 'open' : 'collapsed'}`}>
                <div className="sidebar-header">
                    <img src={AxipaysFull_log} alt="Axipays full logo" />
                </div>

                <div className="sidebar-body">
                    <div className="scrollable-content">
                        {sections.map(section => (
                            <div key={section.section} className="sidebar-section">
                                <h4 className="section-title">{section.section}</h4>
                                {section.items.map(item => <SidebarItem key={item.name} item={item} />)}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="sidebar-footer" />
            </div>
        </>
    );
}

export default Sidebar;
